import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Calling from "../../api/Calling";
import {
    FiSettings,
    FiLink,
    FiKey,
    FiCode,
    FiMap,
    FiSave,
    FiRefreshCw,
    FiLoader,
    FiAlertCircle,
    FiPhone,
    FiUser,
    FiMapPin,
    FiDisc,
    FiCreditCard,
    FiHash,
    FiChevronDown,
    FiChevronUp,
    FiCheckCircle,
    FiInfo
} from "react-icons/fi";
import { MdEmail } from "react-icons/md";

const ApiConfig = () => {
    const [config, setConfig] = useState({
        apiUrl: "",
        apiKey: "",
        responsePath: "data.data",
        mappings: {
            mobile: "mobile",
            alt_number: "alt_number",
            name: "name",
            address: "address",
            circle: "circle",
            fname: "fname",
            id: "id",
            email:"email",
            sub_name:"sub_name",
        },
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showMappings, setShowMappings] = useState(true);
    const [savedSuccess, setSavedSuccess] = useState(false);

    // Mapping icons configuration
    const mappingIcons = {
        mobile: <FiPhone className="text-main-blue" />,
        alt_number: <FiPhone className="text-main-blue" />,
        name: <FiUser className="text-main-blue" />,
        address: <FiMapPin className="text-main-blue" />,
        circle: <FiDisc className="text-main-blue" />,
        fname: <FiUser className="text-main-blue" />,
        id: <FiCreditCard className="text-main-blue" />,
        email: <MdEmail  className="text-main-blue" />,
        sub_name: <FiUser className="text-main-blue" />,
    };

    const mappingDescriptions = {
        mobile: "Mobile number field from API response",
        alt_number: "Alternate mobile number field from API response",
        name: "Name field from API response",
        address: "Address field from API response",
        circle: "Carrier/Circle field from API response",
        fname: "Father's name field from API response",
        id: "Unique ID/UID field from API response",
        email: "Email field from API response",
        sub_name: "Subscriber name field from API response",
    };

    // Fetch Existing Config
    const fetchConfig = async () => {
        setInitialLoading(true);
        setError(null);
        try {
            const resp = await Calling.getApiConfig();
            if (resp?.status) {
                setConfig(resp.data);
            } else {
                setError(resp?.message || "Failed to load API config");
            }
        } catch (err) {
            setError("Something went wrong while fetching config");
        }
        setInitialLoading(false);
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    // Validate form before save
    const validateForm = () => {
        if (!config.apiUrl.trim()) {
            toast.error("API URL is required");
            return false;
        }
        if (!config.responsePath.trim()) {
            toast.error("Response Path is required");
            return false;
        }
        return true;
    };

    // Save / Update Config
    const handleSave = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setSavedSuccess(false);

        try {
            const resp = await Calling.putApiConfig(config);

            if (resp?.status) {
                toast.success(resp.message || "API Configuration saved");
                setSavedSuccess(true);
                setTimeout(() => setSavedSuccess(false), 3000);
            } else {
                toast.error(resp?.message || "Failed to save config");
            }
        } catch (err) {
            toast.error("Something went wrong!");
        }
        setLoading(false);
    };

    // Loading State
    if (initialLoading) {
        return (
            <div className="w-full text-main-font mt-20 px-3 md:ml-[4.4rem]">
                <div className="bg-main-white shadow-md rounded-xl p-6 w-full border border-main-border">
                    <div className="space-y-4">
                        {[...Array(4)].map((_, idx) => (
                            <div key={idx} className="animate-pulse bg-gray-50 rounded-xl p-4 border border-main-border">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                        <div className="h-10 bg-gray-200 rounded w-full"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full text-main-font">
            <div className="bg-main-white shadow-md rounded-xl p-6 w-full border border-main-border">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-main-blue/10 rounded-xl flex items-center justify-center">
                            <FiSettings className="text-main-blue text-xl" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-main-font">API Configuration</h2>
                            <p className="text-sm text-gray-500">
                                Configure external API settings and mappings
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchConfig}
                            disabled={loading || initialLoading}
                            className="p-2.5 bg-main-blue/10 text-main-blue rounded-xl hover:bg-main-blue/20 transition-all"
                            title="Refresh Config"
                        >
                            <FiRefreshCw className={initialLoading ? 'animate-spin' : ''} />
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all
                                ${loading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : savedSuccess
                                        ? 'bg-green-500 text-white'
                                        : 'bg-main-blue text-white hover:bg-main-blue/90'
                                }`}
                        >
                            {loading ? (
                                <>
                                    <FiLoader className="animate-spin" />
                                    Saving...
                                </>
                            ) : savedSuccess ? (
                                <>
                                    <FiCheckCircle />
                                    Saved!
                                </>
                            ) : (
                                <>
                                    <FiSave />
                                    Save Configuration
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-red-50 border border-red-200">
                        <FiAlertCircle className="text-red-500 text-xl flex-shrink-0" />
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                )}

                {/* Info Banner */}
                <div className="flex items-start gap-3 p-4 mb-6 rounded-xl bg-blue-50 border border-blue-200">
                    <FiInfo className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700">
                        <p className="font-medium mb-1">Configuration Guide</p>
                        <p className="text-blue-600">
                            Use <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">{'{number}'}</code> as placeholder for mobile number and{' '}
                        </p>
                    </div>
                </div>

                {/* Configuration Form */}
                <div className="space-y-4">

                    {/* API URL Input */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-main-border">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FiLink className="text-main-blue" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-main-font flex items-center gap-1">
                                    API URL
                                    <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-gray-400 mb-2">External API endpoint with placeholders</p>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 border border-main-border rounded-xl 
                                               focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:border-main-blue
                                               text-sm bg-white font-mono"
                                    placeholder="https://api.example.com?num={number}&key={apikey}"
                                    value={config.apiUrl}
                                    onChange={(e) => setConfig({ ...config, apiUrl: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* API Key Input */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-main-border">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FiKey className="text-main-blue" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-main-font">API Key</label>
                                <p className="text-xs text-gray-400 mb-2">Authentication key for API requests (optional)</p>
                                <input
                                    type="password"
                                    className="w-full px-4 py-2.5 border border-main-border rounded-xl 
                                               focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:border-main-blue
                                               text-sm bg-white"
                                    placeholder="Enter your API key"
                                    value={config.apiKey}
                                    onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Response Path Input */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-main-border">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FiCode className="text-main-blue" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-main-font flex items-center gap-1">
                                    Response Path
                                    <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-gray-400 mb-2">Path to data in API response (e.g., data.data, response.result)</p>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 border border-main-border rounded-xl 
                                               focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:border-main-blue
                                               text-sm bg-white font-mono"
                                    placeholder="data.data"
                                    value={config.responsePath}
                                    onChange={(e) => setConfig({ ...config, responsePath: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Response Mappings Section */}
                    <div className="bg-gray-50 rounded-xl border border-main-border overflow-hidden">
                        {/* Mappings Header */}
                        <button
                            onClick={() => setShowMappings(!showMappings)}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-100 transition-all"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FiMap className="text-main-blue" />
                                </div>
                                <div className="text-left">
                                    <span className="text-sm font-medium text-main-font block">Response Mappings</span>
                                    <span className="text-xs text-gray-400">Map API response fields to database fields</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs bg-main-blue/10 text-main-blue px-2 py-1 rounded-lg font-medium">
                                    {Object.keys(config.mappings).length} fields
                                </span>
                                {showMappings ? (
                                    <FiChevronUp className="text-gray-400" />
                                ) : (
                                    <FiChevronDown className="text-gray-400" />
                                )}
                            </div>
                        </button>

                        {/* Mappings Content */}
                        {showMappings && (
                            <div className="border-t border-main-border p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {Object.keys(config.mappings).map((key) => (
                                        <div
                                            key={key}
                                            className="bg-white rounded-xl p-3 border border-main-border hover:border-main-blue/30 transition-all"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    {mappingIcons[key] || <FiHash className="text-main-blue" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <label className="text-xs font-medium text-main-font capitalize block mb-0.5">
                                                        {key}
                                                    </label>
                                                    <p className="text-xs text-gray-400 mb-2 truncate" title={mappingDescriptions[key]}>
                                                        {mappingDescriptions[key]}
                                                    </p>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-main-border rounded-lg 
                                                                   focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:border-main-blue
                                                                   text-sm bg-gray-50 font-mono"
                                                        placeholder={`Enter ${key} field name`}
                                                        value={config.mappings[key]}
                                                        onChange={(e) =>
                                                            setConfig({
                                                                ...config,
                                                                mappings: { ...config.mappings, [key]: e.target.value },
                                                            })
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions (Mobile) */}
                <div className="mt-6 pt-6 border-t border-main-border md:hidden">
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium transition-all
                            ${loading
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : savedSuccess
                                    ? 'bg-green-500 text-white'
                                    : 'bg-main-blue text-white hover:bg-main-blue/90'
                            }`}
                    >
                        {loading ? (
                            <>
                                <FiLoader className="animate-spin" />
                                Saving...
                            </>
                        ) : savedSuccess ? (
                            <>
                                <FiCheckCircle />
                                Saved Successfully!
                            </>
                        ) : (
                            <>
                                <FiSave />
                                Save Configuration
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApiConfig;